/**
 * Impure shell for the live-updates WebSocket.
 *
 * This hook is the thin, effectful shell around the pure {@link wsReducer}: it
 * gathers inputs (flag, token, online), interprets the reducer's commands
 * against a real `WebSocket` + `setTimeout`, and feeds socket/timer events back
 * in. All lifecycle *logic* lives in the reducer — keep this file mechanical.
 *
 * Flag OFF ⇒ no socket is ever constructed and no timer is ever registered
 * (the feature's kill switch).
 *
 * Live state and data frames are published through the module-level live-updates
 * signal so consumers can react without coupling this shell to react-query.
 */
import { useCallback, useEffect, useRef } from 'react';

import { Config } from 'src/config';
import Auth from 'src/services/api/auth/Auth';
import { EFeatureFlag, useIsFeatureEnabled } from 'src/utils/feature-flags';
import {
    type TimerKey,
    type WsCommand,
    type WsEvent,
} from 'src/services/websocket/ws-protocol';
import { initialWsState, isLive, wsReducer } from 'src/services/websocket/ws-reducer';
import { emitFrame, setLive } from 'src/services/websocket/live-updates-signal';
import { parseIncomingWsFrame } from 'src/services/websocket/ws-frame-parser';

/**
 * The slice of the browser `WebSocket` API the shell drives. Tests inject a
 * fake exposing `send`/`close` and let the test fire `onopen`/`onmessage`/etc.
 */
export interface IWsLike {
    send(data: string): void;
    close(): void;
    onopen: (() => void) | null;
    onmessage: ((event: { data: unknown }) => void) | null;
    onclose: (() => void) | null;
    onerror: (() => void) | null;
}

export type CreateSocket = (url: string) => IWsLike;

const defaultCreateSocket: CreateSocket = (url) => new WebSocket(url) as unknown as IWsLike;

type Timers = Record<TimerKey, number | null>;

/** The event each timer dispatches back into the reducer when it fires. */
const TICK_EVENT: Record<TimerKey, WsEvent> = {
    heartbeat: { type: 'HEARTBEAT_TICK' },
    phase: { type: 'PHASE_TIMEOUT' },
    proactive: { type: 'PROACTIVE_TICK' },
    reconnect: { type: 'RECONNECT_TICK' },
    refreshRetry: { type: 'REFRESH_RETRY_TICK' },
};

const TIMER_KEYS = Object.keys(TICK_EVENT) as TimerKey[];

/** Full jitter: a uniform random delay in `[0, baseDelayMs]`. */
function jitter(baseDelayMs: number): number {
    return Math.random() * baseDelayMs;
}

export function useWebSocket(options?: { createSocket?: CreateSocket }): void {
    const flagEnabled = useIsFeatureEnabled(EFeatureFlag.LIVE_UPDATES);

    // Latest factory, read at OPEN time (kept in a ref so the engine callbacks
    // below can stay stable across renders).
    const createSocketRef = useRef<CreateSocket>(defaultCreateSocket);
    createSocketRef.current = options?.createSocket ?? defaultCreateSocket;

    const stateRef = useRef(initialWsState);
    const socketRef = useRef<IWsLike | null>(null);
    const timersRef = useRef<Timers>({
        heartbeat: null,
        phase: null,
        proactive: null,
        reconnect: null,
        refreshRetry: null,
    });
    const aliveRef = useRef(true);
    const dispatchRef = useRef<(event: WsEvent) => void>(() => {});

    const clearTimer = useCallback((key: keyof Timers) => {
        const id = timersRef.current[key];
        if (id !== null) {
            window.clearTimeout(id);
            timersRef.current[key] = null;
        }
    }, []);

    const teardownSocket = useCallback(() => {
        const socket = socketRef.current;
        if (!socket) {
            return;
        }
        // Detach handlers first so our own close() does not loop back in as a
        // CLOSE event (which would trigger a reconnect).
        socket.onopen = null;
        socket.onmessage = null;
        socket.onclose = null;
        socket.onerror = null;
        socketRef.current = null;
        try {
            socket.close();
        } catch {
            // A socket that never finished opening can throw on close — harmless.
        }
    }, []);

    const interpret = useCallback((command: WsCommand) => {
        const dispatch = (event: WsEvent) => dispatchRef.current(event);
        switch (command.type) {
            case 'OPEN_SOCKET': {
                teardownSocket(); // idempotent: never leak a prior socket (StrictMode-safe)
                const url = Config.wsServer;
                if (!url) {
                    // No server configured (the optional var is absent). Signal
                    // the reducer so it leaves `connecting` instead of stranding.
                    dispatch({ type: 'OPEN_FAILED' });
                    break;
                }
                let socket: IWsLike;
                try {
                    socket = createSocketRef.current(url);
                } catch (err) {
                    // A malformed/unsupported URL throws synchronously here; no
                    // open/close/error handler will ever fire, so feed the failure
                    // back rather than stranding the lifecycle in `connecting`.
                    console.error('Failed to open live-updates WebSocket', err);
                    dispatch({ type: 'OPEN_FAILED' });
                    break;
                }
                socket.onopen = () => dispatch({ type: 'SOCKET_OPEN' });
                socket.onmessage = (event) => {
                    const next = parseIncomingWsFrame(event.data);
                    if (next) {
                        dispatch(next);
                    }
                };
                socket.onclose = () => dispatch({ type: 'CLOSE' });
                socket.onerror = () => dispatch({ type: 'ERROR' });
                socketRef.current = socket;
                break;
            }
            case 'CLOSE_SOCKET':
                teardownSocket();
                break;
            case 'SEND':
                try {
                    socketRef.current?.send(JSON.stringify(command.frame));
                } catch {
                    dispatch({ type: 'ERROR' });
                }
                break;
            case 'SCHEDULE': {
                clearTimer(command.timer);
                // Jitter (when asked) is applied here so the reducer's delay stays deterministic.
                const delayMs = command.jitter ? jitter(command.delayMs) : command.delayMs;
                timersRef.current[command.timer] = window.setTimeout(
                    () => dispatch(TICK_EVENT[command.timer]),
                    delayMs,
                );
                break;
            }
            case 'CLEAR':
                if (command.timer) {
                    clearTimer(command.timer);
                } else {
                    TIMER_KEYS.forEach(clearTimer);
                }
                break;
            case 'REFRESH_TOKEN':
                void (async () => {
                    let outcome: WsEvent = { type: 'REFRESH_FAILED' };
                    try {
                        const refreshedAccessToken = await Auth.refreshAccessToken();
                        const token = Auth.wsToken;
                        if (refreshedAccessToken && token) {
                            outcome = { type: 'REFRESH_SUCCEEDED', token };
                        }
                    } catch {
                        outcome = { type: 'REFRESH_FAILED' };
                    }
                    if (aliveRef.current) {
                        dispatch(outcome);
                    }
                })();
                break;
            case 'EMIT_FRAME':
                emitFrame(command.frame);
                break;
            default: {
                const _: never = command;
                void _;
            }
        }
    }, [clearTimer, teardownSocket]);

    const dispatch = useCallback((event: WsEvent) => {
        const previous = stateRef.current;
        const [next, commands] = wsReducer(previous, event);
        stateRef.current = next;
        if (isLive(next) !== isLive(previous)) {
            setLive(isLive(next));
        }
        commands.forEach(interpret);
    }, [interpret]);
    dispatchRef.current = dispatch;

    useEffect(() => {
        aliveRef.current = true;

        // Read the freshest ws_token; if it is somehow absent while authed (e.g.
        // a session that predates this feature), refresh it.
        const supplyToken = async () => {
            try {
                let token = Auth.wsToken;
                if (!token) {
                    await Auth.refreshAccessToken();
                    token = Auth.wsToken;
                }
                if (token && aliveRef.current) {
                    dispatchRef.current({ type: 'TOKEN_READY', token });
                }
            } catch {
                // Auth.refreshAccessToken normally resolves undefined on failure;
                // contain any unexpected rejection so event handlers never leak it.
            }
        };

        const connectIfReady = () => {
            if (flagEnabled && Auth.accessToken) {
                dispatchRef.current({ type: 'ENABLE' });
                void supplyToken();
            }
        };

        connectIfReady();

        const onLogin = () => connectIfReady();
        const onLogout = () => dispatchRef.current({ type: 'DISABLE' });
        // Regaining connectivity re-supplies the token. Backoff retains its
        // scheduled delay; a healthy live socket ignores the same-token nudge.
        const onOnline = () => {
            if (flagEnabled && Auth.accessToken) {
                void supplyToken();
            }
        };

        document.addEventListener('reffie_login', onLogin);
        document.addEventListener('reffie_logout', onLogout);
        window.addEventListener('online', onOnline);

        return () => {
            aliveRef.current = false;
            document.removeEventListener('reffie_login', onLogin);
            document.removeEventListener('reffie_logout', onLogout);
            window.removeEventListener('online', onOnline);
            dispatchRef.current({ type: 'DISABLE' });
        };
    }, [flagEnabled]);
}
